import React, { useEffect, useRef, useState } from "react"

import Menu from "./Menu"
import { FilterSvg } from "~/ui-rtk/components/ui/svg/essentials"
import { ChevronDownSvg } from "~/ui-rtk/components/ui/svg/arrow"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"

import { useClickOutside } from "~/ui-rtk/shared/hooks"

import { cn } from "~/ui-rtk/utils/tailwind-utils"

import type { TableVisualizationColumnFilter } from "~/ui-rtk/constants/table-visualization-types"
import { TManualFilter } from "../../charts/TableVisualization/constants"
import { TFilterColumnConfig } from "./types"

type TableFiltersProps = {
  columnConfig: TFilterColumnConfig[]
  filterToEdit?: {
    idx: number
    filter: TableVisualizationColumnFilter
  }
  classes?: Partial<{
    container: string
    menu: string
  }>
  additionalFilters?: TManualFilter[]
  onClose: () => void
  onAddFilter?: (columnFilter: TableVisualizationColumnFilter) => void
  onResetFilter?: () => void
  align?: "left" | "right"
  getSelectOptions?: (columnKey: string) => TMultiSelectOption[]
  isLoadingOptions?: boolean
}

const TableFilters: React.FC<TableFiltersProps> = ({
  columnConfig,
  onAddFilter,
  onResetFilter,
  classes,
  filterToEdit,
  additionalFilters,
  onClose,
  align = "right",
  getSelectOptions,
  isLoadingOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)

  const closeMenu = () => {
    setIsOpen(false)
    onClose()
  }

  const toggleMenu = () => {
    setIsOpen(prev => !prev)
  }

  const handleApplyFilters = (filter: TableVisualizationColumnFilter) => {
    const addFilter = onAddFilter
    if (addFilter) {
      addFilter(filter)
      closeMenu()
    }
  }

  const handleResetFilters = () => {
    onResetFilter?.()
  }

  useEffect(() => {
    if (filterToEdit) {
      setIsOpen(true)
    }
  }, [filterToEdit])

  useClickOutside(menuRef, closeMenu)

  return (
    <div
      ref={menuRef}
      className={cn(
        "relative bg-system-blue-700 border border-system-blue-600 h-full items-center flex rounded-system-md py-1.5 px-2",
        classes?.container,
      )}
    >
      <button
        className="flex items-center gap-2 text-sm h-full"
        onClick={toggleMenu}
      >
        <FilterSvg size={12} />
        <span>Filter</span>
        <ChevronDownSvg size={12} />
      </button>

      {isOpen && (
        <Menu
          filterToEdit={filterToEdit}
          columnConfig={columnConfig}
          additionalFilters={additionalFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          align={align}
          getSelectOptions={getSelectOptions}
          isLoadingOptions={isLoadingOptions}
        />
      )}
    </div>
  )
}

export default TableFilters
